import { App, Button, Card, Checkbox, Empty, List, Modal, Spin, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import * as courseApi from '@/api/course';
import type { Course, PreRegisterTier } from '@/api/course';
import { enableCourseApply } from '@/config/branding';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

/** One "✅ …" line of a consent list. */
const ConsentLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex items-start">
        <span className="mr-2">✅</span>
        <Text>{children}</Text>
    </div>
);

const CoursePage: React.FC = () => {
    const { modal } = App.useApp();

    const { t } = useTranslation('common');
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // The course the open modal is about. Both modals act on one card at a
    // time, so a single selection is enough.
    const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
    const [applyModalOpen, setApplyModalOpen] = useState<boolean>(false);
    const [preRegisterModalOpen, setPreRegisterModalOpen] = useState<boolean>(false);
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const fetchCourses = useCallback(async () => {
        try {
            const response = await courseApi.getCourses();
            setCourses(response?.ok ? (response.data ?? []) : []);
        } catch (error) {
            console.error('Failed to fetch courses:', error);
            setCourses([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const closeModals = () => {
        setApplyModalOpen(false);
        setPreRegisterModalOpen(false);
        setSelectedCourse(null);
        setTermsAccepted(false);
    };

    const openApplyModal = (course: Course) => {
        setSelectedCourse(course);
        setTermsAccepted(false);
        setApplyModalOpen(true);
    };

    const openPreRegisterModal = (course: Course) => {
        setSelectedCourse(course);
        setTermsAccepted(false);
        setPreRegisterModalOpen(true);
    };

    const submitApplication = async () => {
        if (!selectedCourse) return;
        setSubmitting(true);
        try {
            const result = await courseApi.applyForCourse(selectedCourse.id);
            if (result.ok) {
                modal.success({
                    title: t('course.apply_success_title'),
                    content: t('course.apply_success_content'),
                });
            } else if (result.status === 409) {
                modal.info({ content: t('course.apply_already') });
            } else {
                modal.error({
                    title: t('course.apply_failed_title'),
                    content: result.message || t('course.apply_failed_content'),
                });
            }
            // Refresh either way: on success the card changes state, and on a
            // conflict the page was showing something already out of date.
            await fetchCourses();
        } finally {
            setSubmitting(false);
            closeModals();
        }
    };

    const submitPreRegistration = async (tier: PreRegisterTier) => {
        if (!selectedCourse) return;
        setSubmitting(true);
        try {
            const result = await courseApi.preRegisterCourse(selectedCourse.id, tier);
            if (result.ok) {
                modal.success({
                    title: t('course.prereg_success_title'),
                    content: t('course.prereg_success_content'),
                });
            } else if (result.status === 409) {
                modal.info({ content: t('course.prereg_already') });
            } else {
                modal.error({
                    title: t('course.prereg_failed_title'),
                    content: result.message || t('course.prereg_failed_content'),
                });
            }
            await fetchCourses();
        } finally {
            setSubmitting(false);
            closeModals();
        }
    };

    const setAuthCookie = () => {
        const token = localStorage.getItem('token');
        const rootDomain = location.hostname.split('.').slice(-2).join('.');
        // Delete both the domain-scoped and domain-less variants to avoid duplicates
        document.cookie = `auth_token=;Path=/;Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        document.cookie = `auth_token=;Path=/;Domain=.${rootDomain};Expires=Thu, 01 Jan 1970 00:00:00 GMT;Secure;SameSite=None`;
        document.cookie = `auth_token=${token};Path=/;Domain=.${rootDomain};Secure;SameSite=None`;
    };

    const viewCourseDetails = (url: string) => {
        // Check if cookie consent is given
        const cookieConsent = localStorage.getItem('cookieConsent');
        if (!cookieConsent) {
            modal.info({
                title: t('course.cookie_required_title'),
                okText: t('course.cookie_accept'),
                content: t('course.cookie_content'),
                onOk() {
                    localStorage.setItem('cookieConsent', 'true');
                    setAuthCookie();
                    window.open(url, '_blank');
                },
                closable: true,
            });
        } else {
            setAuthCookie();
            window.open(url, '_blank');
        }
    };

    /**
     * The single action a card offers. Without the apply/pre-register flow
     * (the Lab default, enableCourseApply off) the card offers exactly what it
     * always did: a View link that is disabled until the course is available.
     * With the flow enabled, a paid course never shows "View": it is unlocked
     * out of band once payment is settled, at which point it has `available`
     * and falls through to the same View button as a free one.
     */
    const cardAction = (course: Course): React.ReactNode => {
        if (!enableCourseApply) {
            return (
                <Button disabled={!course.available} type="link" onClick={() => viewCourseDetails(course.url)}>
                    {t('course.view')}
                </Button>
            );
        }

        if (course.available) {
            return (
                <Button type="link" onClick={() => viewCourseDetails(course.url)}>
                    {t('course.view')}
                </Button>
            );
        }

        if (course.type === 'preregister') {
            return course.pre_registered ? (
                <Button type="link" disabled>{t('course.prereg_done')}</Button>
            ) : (
                <Button type="link" onClick={() => openPreRegisterModal(course)}>
                    {t('course.prereg')}
                </Button>
            );
        }

        return course.applied ? (
            <Button type="link" disabled>{t('course.apply_pending')}</Button>
        ) : (
            <Button type="link" onClick={() => openApplyModal(course)}>
                {t('course.apply')}
            </Button>
        );
    };

    return (
        <div className="p-6 w-full h-full overflow-auto">
            <div className="mb-6">
                <Title level={2} className="mb-2">{t('course.title')}</Title>
                <Paragraph>
                    {t('course.subtitle')}
                </Paragraph>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : courses.length > 0 ? (
                <List
                    grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 4 }}
                    dataSource={courses}
                    renderItem={course => (
                        <List.Item>
                            <Card
                                title={course.title}
                                hoverable
                                actions={[cardAction(course)]}
                            >
                                <Paragraph ellipsis={{ rows: 3 }}>
                                    {course.description}
                                </Paragraph>
                            </Card>
                        </List.Item>
                    )}
                />
            ) : (
                <Empty description={t('course.empty')} />
            )}

            {enableCourseApply && <>
                <Modal
                    title={t('course.apply_modal_title')}
                    open={applyModalOpen}
                    onCancel={closeModals}
                    width={700}
                    footer={false}
                    destroyOnHidden
                >
                    <div className="space-y-6">
                        <div>
                            <Title level={3} className="text-center mb-4">
                                {t('course.apply_heading')}
                            </Title>
                            <Paragraph className="text-center mb-6">
                                {t('course.apply_intro')}
                            </Paragraph>
                        </div>

                        <div>
                            <Paragraph className="mb-4">
                                <Text strong>{t('course.confirm_prompt')}</Text>
                            </Paragraph>
                            <div className="space-y-3 mb-6">
                                <ConsentLine>{t('course.term_real_name')}</ConsentLine>
                                <ConsentLine>{t('course.term_university_email')}</ConsentLine>
                                <ConsentLine>{t('course.term_no_redistribute')}</ConsentLine>
                                <ConsentLine>{t('course.term_newsletter')}</ConsentLine>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-[#2A2A2A] p-4 rounded-lg">
                            <Paragraph className="mb-0 text-sm">
                                {t('course.apply_terms_note')}
                            </Paragraph>
                        </div>

                        <div className="bg-blue-50 dark:bg-[#1F3A3A] p-4 rounded-lg">
                            <div className="flex items-start mb-3">
                                <span className="mr-2">📩</span>
                                <Text strong>{t('course.please_note')}</Text>
                            </div>
                            <Paragraph className="mb-0">
                                {t('course.apply_review_note')}
                            </Paragraph>
                        </div>

                        <div className="border-t pt-4">
                            <div className="flex items-center mb-4">
                                <Checkbox
                                    className="mr-2"
                                    checked={termsAccepted}
                                    onChange={event => setTermsAccepted(event.target.checked)}
                                />
                                <label className="cursor-pointer" onClick={() => setTermsAccepted(v => !v)}>
                                    <Text strong>{t('course.confirm_agree')}</Text>
                                </label>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button onClick={closeModals}>{t('course.cancel')}</Button>
                                <Button
                                    type="primary"
                                    onClick={submitApplication}
                                    loading={submitting}
                                    disabled={!termsAccepted}
                                >
                                    {t('course.apply_submit')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Modal>

                <Modal
                    title={t('course.prereg_modal_title')}
                    open={preRegisterModalOpen}
                    onCancel={closeModals}
                    width={850}
                    footer={false}
                    destroyOnHidden
                >
                    <div className="space-y-6">
                        <div>
                            <Paragraph className="mb-4">
                                <Text strong>{t('course.prereg_confirm_prompt')}</Text>
                            </Paragraph>
                            <div className="space-y-3 mb-6">
                                <ConsentLine>{t('course.term_paid')}</ConsentLine>
                                <ConsentLine>{t('course.term_real_name')}</ConsentLine>
                                <ConsentLine>{t('course.term_no_redistribute')}</ConsentLine>
                                <ConsentLine>{t('course.term_newsletter')}</ConsentLine>
                            </div>
                        </div>

                        <div className="bg-blue-50 dark:bg-[#1F3A3A] p-4 rounded-lg">
                            <div className="flex items-start mb-3">
                                <span className="mr-2">📩</span>
                                <Text strong>{t('course.please_note')}</Text>
                            </div>
                            <Paragraph className="mb-0">
                                {t('course.prereg_payment_note')}
                            </Paragraph>
                        </div>

                        <div className="border-t pt-4">
                            <div className="flex items-center mb-4">
                                <Checkbox
                                    className="mr-2"
                                    checked={termsAccepted}
                                    onChange={event => setTermsAccepted(event.target.checked)}
                                />
                                <label className="cursor-pointer" onClick={() => setTermsAccepted(v => !v)}>
                                    <Text strong>{t('course.confirm_agree')}</Text>
                                </label>
                            </div>
                            <Spin spinning={submitting}>
                                <div className="flex gap-3 flex-wrap justify-between">
                                    <Button
                                        type="default"
                                        onClick={() => submitPreRegistration('normal')}
                                        disabled={!termsAccepted}
                                        style={{ height: 60, width: 250 }}
                                    >
                                        <div className="flex flex-col items-center">
                                            <span>{t('course.prereg_tier_normal')}</span>
                                            <span>{t('course.prereg_tier_normal_price')}</span>
                                        </div>
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={() => submitPreRegistration('premium')}
                                        disabled={!termsAccepted}
                                        style={{ height: 60, width: 250 }}
                                    >
                                        <div className="flex flex-col items-center">
                                            <span>{t('course.prereg_tier_premium')}</span>
                                            <span>{t('course.prereg_tier_premium_price')}</span>
                                        </div>
                                    </Button>
                                    <Button style={{ height: 60, width: 250 }} onClick={closeModals}>
                                        {t('course.cancel')}
                                    </Button>
                                </div>
                            </Spin>
                        </div>
                    </div>
                </Modal>
            </>}
        </div>
    );
};

export default CoursePage;